import PropTypes from "prop-types";
import { Avatar, Box, Button, Paper, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import GetAppIcon from "@mui/icons-material/GetApp";
import ApeakClub from "@/images/apeak_club.png";

const getClubLogo = (club) =>
  club?.logoLightUrl ||
  club?.logoDarkUrl ||
  club?.logoLight ||
  club?.logoDark ||
  club?.logoUrl ||
  club?.logo ||
  ApeakClub;

const getClubPrimaryColor = (club, fallback) =>
  club?.primaryColor ||
  club?.primary_color ||
  club?.brandColor ||
  club?.themeColor ||
  fallback;

const getReadableTextColor = (background) => {
  if (!background || !/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(background)) {
    return "#111111";
  }

  const hex =
    background.length === 4
      ? background
          .slice(1)
          .split("")
          .map((char) => char + char)
          .join("")
      : background.slice(1);
  const red = parseInt(hex.slice(0, 2), 16);
  const green = parseInt(hex.slice(2, 4), 16);
  const blue = parseInt(hex.slice(4, 6), 16);
  const luminance = (0.299 * red + 0.587 * green + 0.114 * blue) / 255;
  return luminance > 0.58 ? "#111111" : "#ffffff";
};

const PwaInstallBanner = ({
  club,
  bottomOffset,
  onDismiss,
  onInstall,
  open,
}) => {
  const theme = useTheme();

  if (!open) return null;

  const accentColor = getClubPrimaryColor(club, theme.palette.primary.main);
  const accentText = getReadableTextColor(accentColor);
  const clubName = club?.name || "clube";
  const logoSrc = getClubLogo(club);

  return (
    <Paper
      role="region"
      aria-label="Convite para instalar o aplicativo"
      elevation={8}
      sx={{
        position: "fixed",
        left: 12,
        right: 12,
        bottom: bottomOffset,
        zIndex: theme.zIndex.snackbar,
        maxWidth: 520,
        mx: "auto",
        borderRadius: 2,
        border: "1px solid",
        borderColor: alpha(accentColor, 0.4),
        bgcolor: "background.paper",
        boxShadow:
          theme.palette.mode === "dark"
            ? "0 18px 45px rgba(0,0,0,0.55)"
            : "0 18px 45px rgba(17,17,17,0.16)",
        overflow: "hidden",
      }}
    >
      <Box sx={{ height: 4, bgcolor: accentColor }} />
      <Stack spacing={1.4} sx={{ p: 1.5 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Avatar
            src={logoSrc}
            alt={clubName}
            variant="rounded"
            sx={{
              width: 44,
              height: 44,
              bgcolor: "action.hover",
              borderRadius: 1.5,
              p: 0.5,
            }}
            imgProps={{ style: { objectFit: "contain" } }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              Instale o Ranking Padel
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Tenha acesso mais rápido ao {clubName} pelo seu celular.
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            fullWidth
            startIcon={<GetAppIcon />}
            onClick={onInstall}
            sx={{
              minHeight: 42,
              textTransform: "none",
              fontWeight: 800,
              bgcolor: accentColor,
              color: accentText,
              "&:hover": { bgcolor: accentColor },
            }}
          >
            Instalar
          </Button>
          <Button
            variant="outlined"
            onClick={onDismiss}
            sx={{
              minHeight: 42,
              minWidth: 112,
              textTransform: "none",
              fontWeight: 800,
            }}
          >
            Agora não
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};

PwaInstallBanner.propTypes = {
  bottomOffset: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  club: PropTypes.object,
  onDismiss: PropTypes.func.isRequired,
  onInstall: PropTypes.func.isRequired,
  open: PropTypes.bool,
};

export default PwaInstallBanner;
